/**
 * 워크스페이스 조회 훅 (TanStack Query)
 *
 * - GET /workspaces            접근 가능 워크스페이스 목록
 * - GET /workspaces/{slug}     워크스페이스 상세(활성모듈·정책)
 *
 * 동적 메타모델: 모듈별 하드코딩 없음. 활성 모듈(enabledModules)은 서버 응답으로만 결정.
 * 서버 응답 시각(UTC)을 그대로 사용 — 클라이언트 로컬 시각 생성 금지.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Workspace } from "@eqms/shared-types";

import { apiFetch } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

/** GET /workspaces 목록 응답 항목 (계약서 1.3절) */
export interface WorkspaceListItem extends Pick<Workspace, "id" | "slug" | "name"> {
  /** 회사(tenant) id */
  companyId?: string;
  isActive?: boolean;
}

/** GET /workspaces/{slug} 상세 응답 (활성모듈·GxP 정책 포함) */
export interface WorkspaceDetail extends WorkspaceListItem {
  /** company_modules 프로비저닝 기반 활성 모듈 코드 목록 */
  enabledModules: string[];
  /**
   * 프로비저닝 구성 여부. true=company_modules 행 존재(enabledModules 필터 적용),
   * false=미구성(전체 노출). enabledModules=[]와 "미구성"을 구분하기 위한 플래그.
   */
  modulesProvisioned?: boolean;
  /** GxP 정책 */
  gxpPolicy?: {
    esignRequired: boolean;
    auditTrailRetentionYears: number;
  };
}

/** 사용자가 멤버인 전 회사의 워크스페이스 평면 목록(조직 스위처). GET /workspaces/mine. */
export function useMyWorkspaces() {
  return useQuery({
    queryKey: queryKeys.workspaces.mine(),
    queryFn: () => apiFetch<WorkspaceListItem[]>("/api/workspaces/mine"),
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/** 접근 가능 워크스페이스 목록 (현재 활성 회사) */
export function useWorkspaces() {
  return useQuery({
    queryKey: queryKeys.workspaces.all(),
    queryFn: () => apiFetch<WorkspaceListItem[]>("/api/workspaces"),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/** 워크스페이스 상세 (slug 기준, 활성모듈·정책) */
export function useWorkspaceDetail(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workspaces.detail(slug ?? ""),
    queryFn: () =>
      apiFetch<WorkspaceDetail>(`/api/workspaces/${encodeURIComponent(slug ?? "")}`, {
        workspaceSlug: slug,
      }),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/** 워크스페이스 이름 변경 (PUT /workspaces/{slug}). 식별자(slug)는 불변 — 이름만 수정. */
export function useUpdateWorkspace(slug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch<WorkspaceDetail>(`/api/workspaces/${encodeURIComponent(slug ?? "")}`, {
        method: "PUT",
        body,
        workspaceSlug: slug,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.workspaces.detail(slug ?? "") });
      void qc.invalidateQueries({ queryKey: queryKeys.workspaces.all() });
    },
  });
}

/** 워크스페이스 활성/보관 전환 (PUT /workspaces/{slug}/active). */
export function useSetWorkspaceActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, active }: { slug: string; active: boolean }) =>
      apiFetch<void>(`/api/workspaces/${encodeURIComponent(slug)}/active`, {
        method: "PUT",
        body: { active },
        workspaceSlug: slug,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.workspaces.all() });
      void qc.invalidateQueries({ queryKey: ["workspaces", "archived"] });
    },
  });
}

/** 보관된 워크스페이스 목록 (GET /workspaces/archived). */
export function useArchivedWorkspaces() {
  return useQuery({
    queryKey: ["workspaces", "archived"],
    queryFn: () => apiFetch<WorkspaceListItem[]>("/api/workspaces/archived"),
    staleTime: 60 * 1000,
  });
}
